import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { DedicationFormValues } from 'schemas';
import { DedicationFormSchema, DEDICATION_MAX_LENGTH } from 'schemas';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Heading2, Small, Muted } from '@/components/Typography';
import { dedicationFormMessages } from './dedicationForm.messages';
import { sharedMessages } from '@/shared-messages/shared.messages';

interface DedicationFormProps {
  onSubmit: (data: DedicationFormValues) => void;
  onBack: () => void;
  isSubmitting: boolean;
}

export function DedicationForm({
  onSubmit,
  onBack,
  isSubmitting,
}: DedicationFormProps) {
  const form = useForm<DedicationFormValues>({
    resolver: zodResolver(DedicationFormSchema),
    defaultValues: {
      dedication: '',
    },
    mode: 'onChange',
  });

  const dedicationValue = form.watch('dedication') || '';

  const handleSubmit = (data: DedicationFormValues) => {
    onSubmit(data);
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <Heading2 className="mb-4">{dedicationFormMessages.title}</Heading2>
        <Muted>{dedicationFormMessages.subtitle}</Muted>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="dedication"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{dedicationFormMessages.dedicationLabel}</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    value={field.value ?? ''}
                    placeholder={dedicationFormMessages.dedicationPlaceholder}
                    className="min-h-[120px] resize-none"
                    maxLength={DEDICATION_MAX_LENGTH}
                  />
                </FormControl>
                <Small>
                  {dedicationFormMessages.characterCount(
                    dedicationValue.length,
                    DEDICATION_MAX_LENGTH
                  )}
                </Small>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-between pt-4">
            <Button type="button" variant="secondary" onClick={onBack}>
              {sharedMessages.commonButtons.back}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? sharedMessages.loadingStates.makingMagic
                : sharedMessages.commonButtons.create}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
